/**
 * Touch input: a left virtual joystick, a right-hand swipe area for the camera,
 * and large action buttons.
 *
 * Uses Pointer Events throughout. Touch events and mouse events disagree about
 * capture and about what happens when a finger leaves the element; pointer
 * capture solves both, and the same code then works for a stylus.
 */

import type { GameAction, InputSource, InputState } from './index.js';

/** Below this the stick is treated as centred, so a resting thumb does not drift. */
const DEADZONE = 0.14;
/** Pixels from centre for full deflection. */
const STICK_RADIUS = 62;
/** Past this fraction of deflection the player runs — no separate run button. */
const RUN_THRESHOLD = 0.82;

export interface TouchOptions {
  /** Radians per pixel of swipe. */
  sensitivity: number;
  invertY: boolean;
}

export interface TouchControlElements {
  /** Container the joystick is drawn into. */
  stickZone: HTMLElement;
  stickKnob: HTMLElement;
  /** The right side of the screen, used for camera swipe and tap-to-walk. */
  lookZone: HTMLElement;
  /** Needed to turn a tap into canvas coordinates the picker can use. */
  canvas: HTMLCanvasElement;
  buttons: ReadonlyArray<{ element: HTMLElement; action: GameAction | 'jump' }>;
}

export function createTouchControls(
  state: InputState,
  elements: TouchControlElements,
  options: TouchOptions
): InputSource {
  let stickPointer: number | null = null;
  let stickOrigin: { x: number; y: number } | null = null;
  let lookPointer: number | null = null;
  let lastLook: { x: number; y: number } | null = null;

  const resetStick = (): void => {
    stickPointer = null;
    stickOrigin = null;
    state.moveX = 0;
    state.moveZ = 0;
    state.running = false;
    elements.stickKnob.style.transform = 'translate(-50%, -50%)';
  };

  const onStickDown = (e: PointerEvent): void => {
    if (stickPointer !== null) return;
    stickPointer = e.pointerId;
    // The stick appears where the thumb lands rather than at a fixed spot —
    // far more forgiving than making people find a painted circle.
    stickOrigin = { x: e.clientX, y: e.clientY };
    elements.stickZone.setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  const onStickMove = (e: PointerEvent): void => {
    if (e.pointerId !== stickPointer || stickOrigin === null) return;

    const dx = e.clientX - stickOrigin.x;
    const dy = e.clientY - stickOrigin.y;
    const distance = Math.hypot(dx, dy);
    const clamped = Math.min(distance, STICK_RADIUS);
    const angle = Math.atan2(dy, dx);

    const knobX = Math.cos(angle) * clamped;
    const knobY = Math.sin(angle) * clamped;
    elements.stickKnob.style.transform =
      `translate(calc(-50% + ${knobX}px), calc(-50% + ${knobY}px))`;

    const magnitude = clamped / STICK_RADIUS;
    if (magnitude < DEADZONE) {
      state.moveX = 0;
      state.moveZ = 0;
      state.running = false;
      return;
    }

    // Rescale past the deadzone so movement starts at zero rather than jumping.
    const scaled = (magnitude - DEADZONE) / (1 - DEADZONE);
    state.moveX = Math.cos(angle) * scaled;
    state.moveZ = -Math.sin(angle) * scaled; // screen Y is down, world Z is forward
    state.running = magnitude >= RUN_THRESHOLD;
    e.preventDefault();
  };

  const onStickUp = (e: PointerEvent): void => {
    if (e.pointerId !== stickPointer) return;
    if (elements.stickZone.hasPointerCapture(e.pointerId)) {
      elements.stickZone.releasePointerCapture(e.pointerId);
    }
    resetStick();
  };

  /* A tap on the look zone is a tap on the world, not a camera drag.
     Distinguished after the fact rather than up front: the finger has to move
     first for it to be a drag, so remembering where and when it went down and
     deciding on release is the only way to have both gestures on one zone. */
  let lookStart: { x: number; y: number; at: number } | null = null;
  const TAP_SLOP_PX = 12;
  const TAP_MS = 350;

  const onLookDown = (e: PointerEvent): void => {
    if (lookPointer !== null) return;
    lookPointer = e.pointerId;
    lastLook = { x: e.clientX, y: e.clientY };
    lookStart = { x: e.clientX, y: e.clientY, at: performance.now() };
    elements.lookZone.setPointerCapture(e.pointerId);
  };

  const onLookMove = (e: PointerEvent): void => {
    if (e.pointerId !== lookPointer || lastLook === null) return;
    const dx = e.clientX - lastLook.x;
    const dy = e.clientY - lastLook.y;
    lastLook = { x: e.clientX, y: e.clientY };
    state.lookYaw += dx * options.sensitivity;
    state.lookPitch += (options.invertY ? -dy : dy) * options.sensitivity;
    e.preventDefault();
  };

  const onLookUp = (e: PointerEvent): void => {
    if (e.pointerId !== lookPointer) return;
    if (elements.lookZone.hasPointerCapture(e.pointerId)) {
      elements.lookZone.releasePointerCapture(e.pointerId);
    }

    if (lookStart !== null) {
      const moved = Math.hypot(e.clientX - lookStart.x, e.clientY - lookStart.y);
      const quick = performance.now() - lookStart.at <= TAP_MS;
      if (moved <= TAP_SLOP_PX && quick) {
        const rect = elements.canvas.getBoundingClientRect();
        state.worldClicks.push({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      }
    }

    lookPointer = null;
    lastLook = null;
    lookStart = null;
  };

  const buttonHandlers: Array<{ el: HTMLElement; down: (e: PointerEvent) => void }> = [];

  return {
    attach() {
      elements.stickZone.addEventListener('pointerdown', onStickDown);
      elements.stickZone.addEventListener('pointermove', onStickMove);
      elements.stickZone.addEventListener('pointerup', onStickUp);
      // pointercancel fires when the browser steals the gesture (a scroll, a
      // system swipe). Without it the stick sticks and the player runs forever.
      elements.stickZone.addEventListener('pointercancel', onStickUp);

      elements.lookZone.addEventListener('pointerdown', onLookDown);
      elements.lookZone.addEventListener('pointermove', onLookMove);
      elements.lookZone.addEventListener('pointerup', onLookUp);
      elements.lookZone.addEventListener('pointercancel', onLookUp);

      for (const { element, action } of elements.buttons) {
        const down = (e: PointerEvent): void => {
          if (action === 'jump') state.jumpQueued = true;
          else state.actions.add(action);
          element.classList.add('pressed');
          window.setTimeout(() => element.classList.remove('pressed'), 120);
          e.preventDefault();
        };
        element.addEventListener('pointerdown', down);
        buttonHandlers.push({ el: element, down });
      }
    },

    detach() {
      elements.stickZone.removeEventListener('pointerdown', onStickDown);
      elements.stickZone.removeEventListener('pointermove', onStickMove);
      elements.stickZone.removeEventListener('pointerup', onStickUp);
      elements.stickZone.removeEventListener('pointercancel', onStickUp);

      elements.lookZone.removeEventListener('pointerdown', onLookDown);
      elements.lookZone.removeEventListener('pointermove', onLookMove);
      elements.lookZone.removeEventListener('pointerup', onLookUp);
      elements.lookZone.removeEventListener('pointercancel', onLookUp);

      for (const { el, down } of buttonHandlers) el.removeEventListener('pointerdown', down);
      buttonHandlers.length = 0;
      resetStick();
    }
  };
}
